import PropTypes from 'prop-types';
import React, { Component, useEffect, useState } from 'react';

import { ChevronDown, ChevronUp } from 'lucide-react';
import { Row, Col, Label, FormGroup, Input, InputGroup, InputGroupText, Select, Card, CardHeader, CardContent, CardTitle, Separator, Button } from 'twyne-new.ui';

import Checkbox from 'components/Checkbox';
import InfoTip from 'components/InfoTip';
import { PureSwitch } from 'components/form/Switch';
import { defaultOptions, getCustomOptions } from 'components/rules/options';
import datasource from 'modules/settings/stores/datasource';

import { useFormStore } from '../../store';
import { extraRuleFields } from '../fieldsUtil';

const propTypes = {
  col: PropTypes.object,
  onChange: PropTypes.func,
};

export default class DatasourceComponent extends Component {
  datasources_options = null
  field_options = null

  loadDatasources = async () => {
    if (this.datasources_options)
      return this.datasources_options

    const { data } = await datasource.all()
    this.datasources_options = data
      .map(({ slug, name, request_config }) => ({
        mergetags: request_config && request_config.mergetags && request_config.mergetags.map(item => [item]) || [],
        label: `[${slug}] - ${name}`,
        value: slug
      }))

    return this.datasources_options
  }

  loadFieldOptions = async () => {
    return this.field_options = this.field_options || this.separateGroupsByLabel(defaultOptions.map(({ key, name, type, group }) => ({
      value: key, group,
      label: name,
      disabled: ['offer', 'form'].includes(type)
    }))
      .concat((await getCustomOptions()).map(({ label, type, group, ...cf }) => ({
        label: group === 'Custom Fields' ? `${label} (${type})` : label,
        type, group,
        ...cf
      })))
      .concat(extraRuleFields))
  }

  separateGroupsByLabel = (opts) => {
    return [
      { value: 'group', label: '-- Traffic --', group: 'Traffic', disabled: true },
      ...opts.filter(opt => opt.group === 'Traffic'),

      { value: 'group', label: '-- Verifications --', group: 'Verifications', disabled: true },
      ...opts.filter(opt => opt.group === 'Verifications'),

      { value: 'group', label: '-- Day and Time --', group: 'Day and Time', disabled: true },
      ...opts.filter(opt => opt.group === 'Day and Time'),

      { value: 'group', label: 'User Fields --', group: 'User Fields', disabled: true },
      ...opts.filter(opt => opt.group === 'User Fields'),

      { value: 'group', label: '-- Custom Fields --', group: 'Custom Fields', disabled: true },
      ...opts.filter(opt => opt.group === 'Custom Fields'),
    ]
  }

  SelectField = ({ onChange, ...props }) => {
    const [options, setOptions] = useState([])

    const loadOptions = async () => {
      const options = await this.loadFieldOptions()
      setOptions(options)
    }
    useEffect(() => { loadOptions() }, [])

    const handleChange = ({ value }) => onChange && onChange(value)

    return <Select required {...props}
      clearable={false}
      options={options}
      onChange={handleChange}
    />
  }

  SelectDatasource = ({ ...props }) => {
    const [options, setOptions] = useState([])

    const loadOptions = async () => {
      const options = await this.loadDatasources()
      setOptions(options)
    }
    useEffect(() => { loadOptions() }, [])

    return <Col>
      <FormGroup>
        <Label>Datasource</Label>
        <Select required {...props}
          clearable={false}
          options={options}
        />
      </FormGroup>
    </Col>
  }

  FieldItem = ({ data, onChange }) => {
    const [mergetag, value, required] = data

    const handleChangeValue = value => {
      data[1] = value
      onChange()
    }

    const handleChangeRequired = checked => {
      data[2] = checked
      onChange()
    }

    return <Row className='items-center'>
      <Col size='5'>
        <FormGroup>
          <Input disabled value={mergetag} />
        </FormGroup>
      </Col>
      <Col size='5'>
        <FormGroup>
          <this.SelectField
            placeholder="select a field"
            value={value}
            onChange={handleChangeValue}
          />
        </FormGroup>
      </Col>
      <Col size='2'>
        <FormGroup>
          <Checkbox
            onChange={handleChangeRequired}
            value={required}
          >
            Required
          </Checkbox>
        </FormGroup>
      </Col>
    </Row>
  }

  Mergetags = ({ data, onChange }) => <Col size='12'>
    <Card>
      <CardHeader>
        <CardTitle>Dependencies</CardTitle>
      </CardHeader>
      <Separator className='my-2' />
      <CardContent >
        {data.map((item, idx) => <this.FieldItem key={idx}
          data={item}
          onChange={() => {
            onChange({})
          }}
        />)}
      </CardContent>
    </Card>
  </Col>

  UpdateDelay = ({ value, onChange }) => <Col size="3">
    <FormGroup>
      <Label>
        API Call
        <InfoTip side='right'>This is the milliseconds to wait until calling the next API with users selection.</InfoTip>
      </Label>
      <Input
        type='number'
        required
        value={value}
        onChange={evt => onChange(evt.target.value)}
      />
    </FormGroup>
  </Col>

  Feedback = ({ type, label = `${type} Feedback`, value, onChange }) => {
    const { datasourceTemplates } = useFormStore()
    const [enabled, setEnabled] = useState(true)

    useEffect(() => { setEnabled(value !== undefined) }, [value])

    const handleChangeEnabled = enabled => {
      onChange(enabled ? '' : undefined)

      setEnabled(enabled)
    }

    const handleChange = option => {
      const value = option && option.value || ''
      onChange(value)
    }

    const loadTemplateOptions = datasourceTemplates[type]

    return <Col size='4'>
      <FormGroup>
        <Label>{label}</Label>
        <InputGroup>
          <InputGroupText addonType='append'>
            <Checkbox
              onChange={handleChangeEnabled}
              value={enabled}
            />
          </InputGroupText>
          {!enabled ? <Input disabled placeholder='disabled' className='italic' /> : <Select
            async
            loadOptions={loadTemplateOptions}
            value={value || ''}
            onChange={handleChange}
            placeholder='Default'
          />
          }
        </InputGroup>
      </FormGroup>
    </Col>
  }

  RemoveBlankOptions = ({ value, onChange }) => <Col size='2'>
    <FormGroup>
      <Label>Remove Blank Options</Label>
      <PureSwitch value={value} onChange={evt => onChange(evt.target.checked)} />
    </FormGroup>
  </Col>

  HideWhenEmpty = ({ value, onChange }) => <Col size='2'>
    <FormGroup>
      <Label>Hide When Options Empty</Label>
      <PureSwitch value={value} onChange={evt => onChange(evt.target.checked)} />
    </FormGroup>
  </Col>

  Order = ({ value, onChange }) => {
    const [field, setField] = useState()
    const [order, setOrder] = useState(ORDERS.asc)

    useEffect(() => {
      if (value) {
        const [field, order] = value.split(' ')
        if (field && order) {
          setField(field)
          setOrder(ORDERS[order])
        }
      }
    }, [value])

    useEffect(() => {
      if (field) {
        onChange(`${field} ${ORDERS[order]}`)
      } else if (field === null) {
        onChange(undefined)
      }
    }, [field, order])

    const handleChangeField = option => {
      const value = option && option.value || null
      setField(value)
    }

    const handleToggleOrder = () => {
      setOrder(order => {
        order = !order
        return order
      })
    }

    return <Col size='5'>
      <FormGroup>
        <Label>Order by</Label>
        <InputGroup>
          <Select options={ORDER_OPTIONS}
            value={field}
            onChange={handleChangeField}
            placeholder='disabled'
          />
          {!field ? null : <>
            <Button variant='light' color='accent' onClick={handleToggleOrder}>
              {order === ORDERS.desc ? <ChevronDown size={16} /> : <ChevronUp size={16} />} {ORDERS[order]}
            </Button>
          </>}
        </InputGroup>
      </FormGroup>
    </Col>
  }

  Content = ({ data, onChange }) => {
    const [slug, setSlug] = useState('')
    const [delay, setDelay] = useState(500)
    const [mergetags, setMergetags] = useState([])
    const [orderBy, setOrderBy] = useState(null)
    const [feedbackLoading, setFeedbackLoading] = useState()
    const [feedbackEmpty, setFeedbackEmpty] = useState()
    const [searchFilter, setSearchFilter] = useState()
    const [hideWhenEmpty, setHideWhenEmpty] = useState()
    const [removeBlank, setRemoveBlank] = useState()

    useEffect(() => {
      const {
        slug, mergetags, delay, orderBy,
        hide_when_empty: hideWhenEmpty,
        remove_blank: removeBlank,
        feedback_loading: feedbackLoading,
        feedback_empty: feedbackEmpty,
        search_filter: searchFilter,
      } = data || {}

      slug && setSlug(slug)
      mergetags && setMergetags(mergetags)
      delay && setDelay(delay)
      orderBy && setOrderBy(orderBy)
      setFeedbackLoading(feedbackLoading)
      setFeedbackEmpty(feedbackEmpty)
      setSearchFilter(searchFilter)
      setHideWhenEmpty(!!hideWhenEmpty)
      setRemoveBlank(!!removeBlank)
    }, [data])

    const handleChange = state => onChange({
      slug, mergetags, delay, orderBy,
      feedback_loading: feedbackLoading,
      feedback_empty: feedbackEmpty,
      search_filter: searchFilter,
      hide_when_empty: hideWhenEmpty,
      remove_blank: removeBlank,
      ...state
    })

    const handleChangeDatasource = ({ value, mergetags }) => {
      setSlug(value)
      setMergetags(mergetags)
      handleChange({ slug: value, mergetags })
    }

    const buildHandleChange = (key, setValue) => value => {
      setValue(value)
      handleChange({ [key]: value })
    }

    return <>
      <this.SelectDatasource value={slug} onChange={handleChangeDatasource} />
      <Col size='12'>
        <Row>
          <this.UpdateDelay
            value={delay}
            onChange={(buildHandleChange('delay', setDelay))}
          />
          <this.Order
            value={orderBy}
            onChange={buildHandleChange('orderBy', setOrderBy)}
          />
          <this.RemoveBlankOptions
            value={removeBlank}
            onChange={buildHandleChange('remove_blank', setRemoveBlank)}
          />
          <this.HideWhenEmpty
            value={hideWhenEmpty}
            onChange={buildHandleChange('hide_when_empty', setHideWhenEmpty)}
          />
          <this.Feedback
            value={feedbackLoading}
            onChange={buildHandleChange('feedback_loading', setFeedbackLoading)}
            type='loading'
          />
          <this.Feedback
            value={feedbackEmpty}
            onChange={buildHandleChange('feedback_empty', setFeedbackEmpty)}
            type='empty'
          />
          <this.Feedback
            value={searchFilter}
            onChange={buildHandleChange('search_filter', setSearchFilter)}
            type='search_filter'
            label='Search Filter'
          />
          <this.Mergetags
            data={mergetags}
            onChange={handleChange}
          />
        </Row>
      </Col>
    </>
  }

  Enable = ({ data, onChange, children }) => {
    const [enabled, setEnabled] = useState(false)

    useEffect(() => {
      data && setEnabled(true)
    }, [data])

    const handleEnable = () => {
      setEnabled(enabled => {
        enabled = !enabled
        if (!enabled) {
          onChange(null)
        }
        return enabled
      })
    }

    return <>
      <Col size='12'>
        <FormGroup>
          <Label>Enabled</Label>
          <PureSwitch
            value={enabled}
            onChange={handleEnable}
          />
        </FormGroup>
      </Col>
      {enabled ? children : null}
    </>
  }

  render() {
    const props = {
      onChange: this.props.onChange("field_datasource"),
      data: this.props.col.field_datasource
    }
    return <Row>
      <this.Enable {...props}>
        <this.Content {...props} />
      </this.Enable>
    </Row>
  }
}

DatasourceComponent.propTypes = propTypes
DatasourceComponent.defaultProps = {}

const ORDER_OPTIONS = [
  { value: 'label', label: 'Label' },
  { value: 'value', label: 'Value' },
]

const ORDERS = {
  asc: true,
  desc: false,
  true: "asc",
  false: "desc",
}
